import { useEffect, useMemo } from 'react';
import { Alert, Box } from '@mui/material';
import { People } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ButtonTabs from '../components/ButtonTabs';
import ListHeader from '../components/ListHeader';
import UsersListTab from '../components/users/UsersListTab';
import UserActivitiesTab from '../components/users/UserActivitiesTab';
import { usePermissions } from '../contexts/PermissionContext';
import usePageTitle from '../hooks/usePageTitle';


export default function UsersPage() {
  const { t } = useTranslation(['pages']);
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  usePageTitle(t('pages:users.title'));

  const canReadUsers = hasPermission('users_read');

  const tabs = useMemo(() => {
    if (!canReadUsers) {
      return [];
    }
    return [
      {
        id: 'users',
        label: t('pages:users.tabs.users'),
        content: <UsersListTab />,
      },
      {
        id: 'activity',
        label: t('pages:users.tabs.activity'),
        content: <UserActivitiesTab />,
      },
    ];
  }, [canReadUsers, t]);

  const defaultTab = tabs[0]?.id ?? 'users';
  const selectedTab = searchParams.get('tab');
  const normalizedTab: string = tabs.some((tab) => tab.id === selectedTab)
    ? (selectedTab ?? defaultTab)
    : defaultTab;

  useEffect(() => {
    if (!tabs.length) {
      return;
    }
    if (selectedTab === normalizedTab) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('tab', normalizedTab);
    setSearchParams(next, { replace: true });
  }, [normalizedTab, searchParams, selectedTab, setSearchParams, tabs.length]);

  if (!canReadUsers) {
    return <Alert severity="warning">{t('pages:users.accessDenied')}</Alert>;
  }

  return (
    <Box>
      <ListHeader
        icon={<People sx={{ fontSize: 32, color: 'primary.main' }} />}
        title={t('pages:users.title')}
        subtitle={t('pages:users.subtitle')}
      />
      <ButtonTabs
        tabs={tabs}
        useUrl
        tabParam="tab"
        defaultValue={defaultTab}
        contentSx={{ mt: 3 }}
      />
    </Box>
  );
}
